import shopApi from '@services/shop';
import { usePolling } from './usePolling';
import { CONNECTION_KIND, CONNECTION_WARNINGS } from '../constants';
import { findInventorySource } from '../utils/shopUtils';

/**
 * Polls for stock and product connection status
 * Checks for successful inventory ingestion and triggers callbacks
 *
 * @param {boolean} pollingForStockConnection - Whether to poll for stock connection
 * @param {boolean} pollingForProductConnection - Whether to poll for product connection
 * @param {Object} currentShop - Current shop object
 * @param {Function} onShopRefresh - Called when shop data needs refreshing
 * @param {Function} setPollingForStockConnection - Sets stock polling state
 * @param {Function} setPollingForProductConnection - Sets product polling state
 * @param {Function} setStockConnectionWarning - Sets stock warning message
 * @param {Function} setProductConnectionWarning - Sets product warning message
 */
export const useConnectionPolling = (
	pollingForStockConnection,
	pollingForProductConnection,
	currentShop,
	onShopRefresh,
	setPollingForStockConnection,
	setPollingForProductConnection,
	setStockConnectionWarning,
	setProductConnectionWarning
) => {
	// Poll for stock connection
	usePolling({
		enabled: pollingForStockConnection && !!currentShop,
		fetchFn: async () => {
			const shops = await shopApi.getMyShops();
			return shops.find((s) => s.id === currentShop.id);
		},
		successCondition: (shop) => {
			// findInventorySource handles the shape /api/my-shop actually returns
			// (stockInventorySource) — the bare inventorySources read matched a field
			// the endpoint never sends, so polling always timed out (NEA-5988).
			const stockSource = findInventorySource(shop, CONNECTION_KIND.STOCK);
			return !!stockSource?.latestIngest?.timestamp;
		},
		onSuccess: async () => {
			await onShopRefresh();
			setPollingForStockConnection(false);
		},
		onTimeout: () => {
			setPollingForStockConnection(false);
			setStockConnectionWarning(CONNECTION_WARNINGS.STOCK_TIMEOUT);
		}
	});

	// Poll for product connection
	usePolling({
		enabled: pollingForProductConnection && !!currentShop,
		fetchFn: async () => {
			const shops = await shopApi.getMyShops();
			return shops.find((s) => s.id === currentShop.id);
		},
		successCondition: (shop) => {
			// Product ingests are written by custom-product-processing, whose
			// latestIngest carries customProductTimestamp rather than timestamp —
			// mirror inventory-connect's uploadStatus.js.
			const productSource = findInventorySource(shop, CONNECTION_KIND.PRODUCT);
			return !!(productSource?.latestIngest?.timestamp || productSource?.latestIngest?.customProductTimestamp);
		},
		onSuccess: async () => {
			await onShopRefresh();
			setPollingForProductConnection(false);
		},
		onTimeout: () => {
			setPollingForProductConnection(false);
			setProductConnectionWarning(CONNECTION_WARNINGS.PRODUCT_TIMEOUT);
		}
	});
};
